import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Ban, Copy, CreditCard, FileDown, FileMinus2, IndianRupee, Loader2, Mail,
  MessageCircle, Pencil, Printer, Send, Share2,
} from "lucide-react";
import { toast } from "sonner";
import { API, api, DOC_TYPE_LABELS, download, errMsg, fmtDate, inr } from "@/lib/api";
import { can, useAuth } from "@/context/AuthContext";
import { AttachmentPanel, ConfirmDialog, Field, PageHeader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function PaymentDialog({ invoice, open, onClose, onDone }) {
  const [form, setForm] = useState({ amount: 0, mode: "UPI", reference: "", notes: "", date: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({ amount: invoice?.balance || 0, mode: "UPI", reference: "", notes: "",
      date: new Date().toISOString().slice(0, 10) });
  }, [open, invoice]);
  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post("/payments", { ...form, customer_id: invoice.customer_id,
        invoice_id: invoice.id, booking_id: invoice.booking_id || "" });
      (r.data.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`Receipt ${r.data.payment.receipt_no} recorded`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="record-payment-dialog">
        <DialogHeader><DialogTitle className="font-display">Record Payment — {invoice?.invoice_no}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Amount (₹)" required>
            <Input type="number" min="0" value={form.amount} data-testid="payment-amount-input"
              onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} className="bg-white border-[#E5E0D8]" />
          </Field>
          <Field label="Date">
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="payment-date-input" className="bg-white border-[#E5E0D8]" />
          </Field>
          <Field label="Mode">
            <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
              <SelectTrigger data-testid="payment-mode-select" className="bg-white border-[#E5E0D8]"><SelectValue /></SelectTrigger>
              <SelectContent>{["Cash", "UPI", "Bank Transfer", "Card", "Razorpay", "Other"].map((m) =>
                <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Reference / UTR">
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
              data-testid="payment-reference-input" className="bg-white border-[#E5E0D8]" />
          </Field>
        </div>
        <p className="text-xs text-[#5C6B69]">Balance due: <b className="num">{inr(invoice?.balance)}</b></p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || form.amount <= 0}
            className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid="payment-submit-button">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Record Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cnOpen, setCnOpen] = useState(false);
  const [cnReason, setCnReason] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/invoices/${id}`).then((r) => setData(r.data)).catch((e) => {
      toast.error(errMsg(e));
      navigate("/invoices");
    });
    api.get("/settings/business").then((r) => setTemplate(r.data.invoice_template || "premium")).catch(() => {});
  }, [id, navigate]);

  useEffect(load, [load]);

  if (!data) return <div className="py-24 text-center text-[#5C6B69]"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  const inv = data.invoice;
  const writable = can(user?.role, "invoices", true);
  const isGstDoc = ["tax_invoice", "bill_of_supply", "credit_note", "debit_note"].includes(inv.doc_type);

  const act = async (name, fn) => {
    setBusy(name);
    try {
      await fn();
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy("");
  };

  const shareWhatsApp = async () => {
    await download(`/invoices/${inv.id}/pdf?template=${template}`, "invoice.pdf");
    const text = encodeURIComponent(
      `Invoice ${inv.invoice_no} from Dreamzz Travels — Total ${inr(inv.grand_total)}. PDF downloaded, please attach it here.`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const sendEmail = () =>
    act("email", () => api.post(`/invoices/${inv.id}/send-email`)
      .then((r) => toast.success(`Invoice emailed to ${r.data.to}`)));

  const sendWhatsApp = () =>
    act("wa", () => api.post(`/integrations/whatsapp/send-invoice/${inv.id}`)
      .then(() => toast.success("WhatsApp message accepted by Meta (delivery via webhooks)")));

  const createPayLink = () =>
    act("rzp", () => api.post(`/integrations/razorpay/payment-link/${inv.id}`)
      .then((r) => {
        navigator.clipboard?.writeText(r.data.short_url).catch(() => {});
        toast.success("Razorpay payment link created & copied");
        load();
      }));

  return (
    <div data-testid="invoice-detail-page">
      <PageHeader
        title={<span className="flex items-center gap-3">{inv.invoice_no || "DRAFT"}
          <StatusBadge value={inv.status} testid="invoice-status-badge" /></span>}
        subtitle={`${DOC_TYPE_LABELS[inv.doc_type]} · ${inv.customer?.name} · ${fmtDate(inv.date)} · FY ${inv.fy}`}
        testid="invoice-detail-header"
        actions={
          <>
            <Button variant="outline" onClick={() => navigate(-1)} data-testid="invoice-back-button"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            <Select value={template} onValueChange={setTemplate}>
              <SelectTrigger className="w-44 bg-white border-[#E5E0D8]" data-testid="invoice-template-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="classic">A · Classic Corporate</SelectItem>
                <SelectItem value="minimal">B · Modern Minimal</SelectItem>
                <SelectItem value="premium">C · Premium Dreamzz</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => download(`/invoices/${inv.id}/pdf?template=${template}`, "invoice.pdf")}
              data-testid="invoice-download-pdf"><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
            <Button variant="outline" onClick={() => window.open(`${API}/invoices/${inv.id}/pdf?template=${template}&download=false`, "_blank")}
              data-testid="invoice-print-button"><Printer className="h-4 w-4 mr-1" /> Print</Button>
            <Button variant="outline" onClick={shareWhatsApp} data-testid="invoice-share-button"><Share2 className="h-4 w-4 mr-1" /> Share</Button>
            {writable && (
              <>
                <Button variant="outline" disabled={!!busy} onClick={sendEmail} data-testid="invoice-email-button">
                  {busy === "email" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
                  Email{inv.email_status === "Sent" ? " ✓" : ""}</Button>
                <Button variant="outline" disabled={!!busy} onClick={sendWhatsApp} data-testid="invoice-whatsapp-button">
                  <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp{inv.whatsapp_status === "Sent" ? " ✓" : ""}</Button>
                {inv.balance > 0 && (
                  <Button variant="outline" disabled={!!busy} onClick={createPayLink} data-testid="invoice-paylink-button">
                    <CreditCard className="h-4 w-4 mr-1" /> Payment Link</Button>
                )}
              </>
            )}
          </>
        } />

      {inv.razorpay_short_url && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm flex items-center justify-between flex-wrap gap-2" data-testid="invoice-paylink-banner">
          <span>Razorpay link ({inv.razorpay_status || "created"}): <b className="num">{inv.razorpay_short_url}</b></span>
          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(inv.razorpay_short_url); toast.success("Link copied"); }}>Copy Link</Button>
        </div>
      )}

      {writable && (
        <div className="flex flex-wrap gap-2 mb-6">
          {inv.status === "draft" && (
            <Button size="sm" className="bg-[#0D4F4A] hover:bg-[#136A63]" disabled={!!busy}
              onClick={() => act("issue", () => api.post(`/invoices/${inv.id}/issue`).then(() => toast.success("Invoice issued")))}
              data-testid="invoice-issue-button"><Send className="h-3.5 w-3.5 mr-1" /> Issue Invoice</Button>
          )}
          {["issued", "partial"].includes(inv.status) && inv.balance > 0 && can(user?.role, "payments", true) && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setPayOpen(true)}
              data-testid="invoice-record-payment-button"><IndianRupee className="h-3.5 w-3.5 mr-1" /> Record Payment</Button>
          )}
          {["issued", "partial", "paid"].includes(inv.status) && inv.doc_type === "tax_invoice" && (
            <Button size="sm" variant="outline" onClick={() => setCnOpen(true)} data-testid="invoice-credit-note-button">
              <FileMinus2 className="h-3.5 w-3.5 mr-1" /> Create Credit Note</Button>
          )}
          <Button size="sm" variant="outline" disabled={!!busy}
            onClick={() => act("dup", () => api.post(`/invoices/${inv.id}/duplicate`).then((r) => navigate(`/invoices/${r.data.invoice.id}`)))}
            data-testid="invoice-duplicate-button"><Copy className="h-3.5 w-3.5 mr-1" /> Duplicate</Button>
          {inv.status === "draft" && (
            <Button size="sm" variant="outline" onClick={() => navigate(`/invoices/new?edit=${inv.id}`)}
              data-testid="invoice-edit-button"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit Draft</Button>
          )}
          {["draft", "issued"].includes(inv.status) && inv.paid_amount === 0 && (
            <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => setCancelOpen(true)}
              data-testid="invoice-cancel-button"><Ban className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-6 sm:p-8" data-testid="invoice-preview">
          <div className="flex justify-between items-start border-b-4 border-[#0D4F4A] pb-4 mb-6">
            <div>
              <div className="font-display text-xl font-bold text-[#0D4F4A]">DREAMZZ TRAVELS</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A227]">Travel · Hotels · Resorts</div>
            </div>
            <div className="text-right">
              <div className="font-display text-lg font-bold text-[#0D4F4A]">{DOC_TYPE_LABELS[inv.doc_type]}</div>
              <div className="text-xs text-[#5C6B69]">{inv.invoice_no || "Draft — not issued"}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-6">
            <div><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Invoice Date</div>{fmtDate(inv.date)}</div>
            <div><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Due Date</div>{fmtDate(inv.due_date)}</div>
            <div><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Place of Supply</div>{inv.place_of_supply || "—"} ({inv.place_of_supply_code || "—"})</div>
            <div><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Reverse Charge</div>{inv.reverse_charge ? "Yes" : "No"}</div>
            <div><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Customer Type</div>{inv.customer_type}</div>
            <div><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Tax Type</div>{(inv.tax_type || "").replace("_", " ")}</div>
            {inv.original_invoice_no && <div><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Original Invoice</div>{inv.original_invoice_no}</div>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-[#FAF8F5] rounded-lg p-4 text-sm">
              <div className="text-[10px] uppercase font-bold text-[#C9A227] mb-1">Bill To</div>
              <div className="font-semibold">{inv.customer?.name}</div>
              <div className="text-[#5C6B69] text-xs">{[inv.customer?.address, inv.customer?.city, inv.customer?.state, inv.customer?.pin].filter(Boolean).join(", ")}</div>
              {inv.customer?.gstin && <div className="text-xs mt-1">GSTIN: {inv.customer.gstin}</div>}
              {inv.customer?.phone && <div className="text-xs text-[#5C6B69]">{inv.customer.phone} {inv.customer?.email}</div>}
            </div>
            {inv.booking?.booking_id && (
              <div className="bg-[#FAF8F5] rounded-lg p-4 text-sm">
                <div className="text-[10px] uppercase font-bold text-[#C9A227] mb-1">Travel Details</div>
                <div className="font-semibold">{inv.booking.booking_id} · {inv.booking.package}</div>
                <div className="text-xs text-[#5C6B69]">{inv.booking.destination} · {fmtDate(inv.booking.travel_start)} → {fmtDate(inv.booking.travel_end)}</div>
                <div className="text-xs text-[#5C6B69]">{inv.booking.travellers} traveller(s) {inv.booking.vehicle ? `· ${inv.booking.vehicle}` : ""}</div>
              </div>
            )}
          </div>

          <div className="overflow-x-auto border border-[#E5E0D8] rounded-lg">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="bg-[#0D4F4A] text-white text-xs">
                  {["Sl", "Description", "SAC", "Qty", "Rate", "Disc.", "Taxable", "GST%", "CGST", "SGST", "IGST", "Total"].map((h, i) => (
                    <th key={h} className={`px-2.5 py-2 font-semibold ${i > 1 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inv.items.map((it, i) => (
                  <tr key={i} className={i % 2 ? "bg-[#FAF8F5]" : ""}>
                    <td className="px-2.5 py-2">{it.sl}</td>
                    <td className="px-2.5 py-2">{it.description}</td>
                    <td className="px-2.5 py-2 text-right">{it.sac || "—"}</td>
                    <td className="px-2.5 py-2 text-right">{it.qty} {it.unit}</td>
                    <td className="px-2.5 py-2 text-right num">{Number(it.rate).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    <td className="px-2.5 py-2 text-right num">{Number(it.discount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    <td className="px-2.5 py-2 text-right num">{Number(it.taxable).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    <td className="px-2.5 py-2 text-right num">{it.gst_rate}%</td>
                    <td className="px-2.5 py-2 text-right num">{Number(it.cgst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    <td className="px-2.5 py-2 text-right num">{Number(it.sgst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    <td className="px-2.5 py-2 text-right num">{Number(it.igst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    <td className="px-2.5 py-2 text-right num font-semibold">{Number(it.total).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end mt-4">
            <dl className="w-64 text-sm space-y-1.5">
              {[["Subtotal", inv.subtotal], ["Discount", -inv.discount_total], ["Taxable Value", inv.taxable_total],
                ["CGST", inv.cgst_total], ["SGST", inv.sgst_total], ["IGST", inv.igst_total],
                ["Round Off", inv.round_off]].map(([l, v]) => (
                <div key={l} className="flex justify-between"><dt className="text-[#5C6B69]">{l}</dt><dd className="num">{inr(v)}</dd></div>
              ))}
              <div className="flex justify-between border-t-2 border-[#0D4F4A] pt-2">
                <dt className="font-display font-bold">Grand Total</dt>
                <dd className="num font-bold text-lg text-[#0D4F4A]" data-testid="invoice-detail-grand-total">{inr(inv.grand_total)}</dd>
              </div>
            </dl>
          </div>
          <p className="text-xs italic text-[#5C6B69] mt-3">{inv.amount_words}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 bg-[#FAF8F5] rounded-lg p-4 text-sm">
            <div><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Invoice Total</div><span className="num font-semibold">{inr(inv.grand_total)}</span></div>
            <div><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Paid / Advance</div><span className="num font-semibold text-emerald-600">{inr(inv.paid_amount)}</span></div>
            <div><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Balance Due</div><span className="num font-semibold text-red-600">{inr(inv.balance)}</span></div>
            <div><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Created By</div>{inv.created_by_name || "—"}</div>
          </div>
          {inv.notes && <p className="text-sm text-[#5C6B69] mt-4"><b>Notes:</b> {inv.notes}</p>}
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5">
            <h3 className="font-display text-base font-semibold mb-3">Payments ({data.payments.length})</h3>
            {data.payments.length === 0 ? <p className="text-xs text-[#5C6B69] italic">No payments recorded.</p> : (
              <ul className="space-y-2">
                {data.payments.map((p) => (
                  <li key={p.id} className="text-sm border-b border-[#E5E0D8]/60 pb-2 last:border-0" data-testid={`invoice-payment-${p.id}`}>
                    <div className="flex justify-between">
                      <button className="text-[#0D4F4A] font-semibold hover:underline"
                        onClick={() => download(`/payments/${p.id}/receipt-pdf`, "receipt.pdf")}>{p.receipt_no}</button>
                      <span className="num font-semibold">{inr(p.amount)}</span>
                    </div>
                    <div className="text-xs text-[#5C6B69]">{fmtDate(p.date)} · {p.mode} {p.reference ? `· ${p.reference}` : ""}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {data.credit_notes.length > 0 && (
            <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5">
              <h3 className="font-display text-base font-semibold mb-3">Credit Notes</h3>
              <ul className="space-y-2">
                {data.credit_notes.map((cn) => (
                  <li key={cn.id} className="text-sm flex justify-between">
                    <button className="text-[#0D4F4A] font-semibold hover:underline" onClick={() => navigate(`/invoices/${cn.id}`)}>{cn.invoice_no}</button>
                    <span className="num">-{inr(cn.grand_total)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5">
            <AttachmentPanel entityType="invoice" entityId={inv.id} testid="invoice-attachments" />
          </div>
          {isGstDoc && (
            <div className="bg-[#0D4F4A]/5 border border-[#0D4F4A]/20 rounded-xl p-4 text-xs text-[#0D4F4A]">
              GST data from this document flows into the Sales Register, B2B/B2C, HSN/SAC and GST Summary reports automatically.
            </div>
          )}
        </div>
      </div>

      <PaymentDialog invoice={inv} open={payOpen} onClose={() => setPayOpen(false)} onDone={load} />
      <ConfirmDialog open={cancelOpen} onClose={() => setCancelOpen(false)} testid="invoice-cancel-confirm"
        title="Cancel this invoice?" description="The invoice number is retained for audit. GST and ledger entries will be reversed. Paid invoices need a credit note instead."
        onConfirm={() => act("cancel", () => api.post(`/invoices/${inv.id}/cancel`, { reason: "Cancelled by user" })
          .then(() => { toast.success("Invoice cancelled"); setCancelOpen(false); }))} />
      <Dialog open={cnOpen} onOpenChange={setCnOpen}>
        <DialogContent data-testid="credit-note-dialog">
          <DialogHeader><DialogTitle className="font-display">Create Credit Note</DialogTitle></DialogHeader>
          <Field label="Reason" required>
            <Textarea value={cnReason} onChange={(e) => setCnReason(e.target.value)}
              data-testid="credit-note-reason" className="bg-white border-[#E5E0D8]"
              placeholder="e.g. Trip cancelled, service not rendered, rate correction" />
          </Field>
          <p className="text-xs text-[#5C6B69]">A full-value credit note will be issued against {inv.invoice_no} ({inr(inv.grand_total)}). Edit items afterwards via a draft if you need a partial note.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCnOpen(false)}>Cancel</Button>
            <Button disabled={!cnReason.trim() || !!busy} className="bg-[#0D4F4A] hover:bg-[#136A63]"
              data-testid="credit-note-submit"
              onClick={() => act("cn", () => api.post(`/invoices/${inv.id}/credit-note`, { reason: cnReason })
                .then((r) => {
                  (r.data.warnings || []).forEach((w) => toast.warning(w));
                  toast.success(`Credit note ${r.data.invoice.invoice_no} created`);
                  setCnOpen(false);
                  navigate(`/invoices/${r.data.invoice.id}`);
                }))}>
              {busy === "cn" && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Create Credit Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
